import { Fragment, type ReactElement, useState } from "react"

import { useTranslation } from "react-i18next"

import { DeleteOutline } from "@mui/icons-material"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import { CircularProgress, Grid, capitalize } from "@mui/material"
import { Button, InputText, Select, Typography } from "@synapse-analytics/synapse-ui"
import { type FormikProps, useFormikContext } from "formik"
import type { MRT_ColumnDef } from "material-react-table"
import { v4 as uuidv4 } from "uuid"

import { ErrorStateWithRefetch } from "../../../components/UI/ErrorStateWithRefetch"
import { InfoBlock } from "../../../components/containers/InfoBlock"
import { InfoContainer } from "../../../components/core/InfoContainer"
import { KeyValueBlock } from "../../../components/dialogs/components/KeyValueBlock"
import { Tick } from "../../../components/status/Tick"
import { TabularJsonTable } from "../../../components/tables/TabularJsonTable/TabularJsonTable"
import {
  type MergeOptions,
  mergeJsonWithArray,
  typeCastBasedOnType,
} from "../../../components/tables/TabularJsonTable/helpers"
import { TemplateFeaturesTable } from "../../../components/tables/TemplateFeaturesTable"
import { ScriptSchemaFeature } from "../../../types/generated/api/ScriptSchemaFeature"
import type {
  BuiltinScriptTestingProps,
  DataRecordForm,
  InitialFormState,
  ScriptSetupProps,
  ScriptTestFeature,
} from "../../Scripts/Interfaces"
import { ScriptTestingLogs } from "../../Scripts/components/ScriptComponents"

import styles from "../MarketPlace.module.scss"

export function IntegrationSetup(props: Readonly<ScriptSetupProps>): ReactElement {
  const { template, script, isScriptInViewMode, workflowSchema, workflowFormik, formik } = props

  const { templateData, isTemplateDataLoading, isTemplateDataFetching, isFetchingTemplateDataError, refetchTemplate } =
    template

  const { scriptData, isScriptDataLoading } = script

  const { t } = useTranslation("scripts", {
    keyPrefix: "builtinScript.components.setup",
  })

  const [page, setPage] = useState<number>(0)
  const rowsPerPage = 5

  const order = [
    Boolean(templateData?.description),
    Boolean(templateData?.schema),
    Boolean(templateData?.schema && templateData?.schema?.output_features?.length > 0),
  ]

  return (
    <Grid container item xs={12} gap={2} direction={"column"} flexWrap={"nowrap"}>
      {isFetchingTemplateDataError ? (
        <Grid container xs>
          <ErrorStateWithRefetch
            onRefetch={() => refetchTemplate()}
            isFetching={isTemplateDataFetching || isTemplateDataLoading}
          />
        </Grid>
      ) : (
        <Fragment>
          {/* Description */}
          {templateData?.description && (
            <Grid container item className={styles.baseBlock}>
              <Grid item className={styles.blockTitle}>
                <Typography variant="label-bold" variantColor={2} data-order>
                  {order.slice(0, 1).filter(Boolean).length}
                </Typography>

                <Typography variant="a">{t("labels.description")}</Typography>
              </Grid>

              <Typography variant="p" variantColor={2}>
                {templateData?.description}
              </Typography>
            </Grid>
          )}

          {/* Creds */}
          <Grid item className={styles.baseBlock}>
            <Grid item className={styles.blockTitle}>
              <Typography variant="label-bold" variantColor={2} data-order>
                {order.slice(0, 2).filter(Boolean).length}
              </Typography>

              <Typography variant="a">{t("credentials.title")}</Typography>
            </Grid>

            {isTemplateDataFetching || isTemplateDataLoading || isScriptDataLoading ? (
              <InfoContainer icon={<CircularProgress />} title={t("credentials.loading")} />
            ) : templateData?.schema && templateData?.schema?.secrets?.length > 0 && formik.values.creds?.length > 0 ? (
              <KeyValueBlock
                isViewMode={isScriptInViewMode}
                formik={formik as FormikProps<InitialFormState | DataRecordForm>}
                isCreds={true}
                usedCreds={scriptData?.active_version.creds_keys}
                valueListOptions={workflowSchema?.filter((feature) => feature?.name?.length > 0)}
                type="Script"
                paginationProps={{
                  usePagination: true,
                  variant: "simple",
                  size: "small",
                  page,
                  rowsPerPage,
                  onPageChange: setPage,
                }}
              />
            ) : (
              <InfoBlock text={t("credentials.emptyState")} bordered type="default" alignText="center" />
            )}
          </Grid>

          {/* Input Features */}
          {templateData?.schema && (
            <Grid item className={styles.baseBlock}>
              <Grid item className={styles.blockTitle}>
                <Typography variant="label-bold" variantColor={2} data-order>
                  {order.slice(0, 3).filter(Boolean).length}
                </Typography>

                <Typography variant="a">{t("inputFeatures.title")}</Typography>
              </Grid>

              <Typography variant="p" variantColor={2}>
                {t("inputFeatures.description")}
              </Typography>

              {isTemplateDataFetching || isTemplateDataLoading || isScriptDataLoading ? (
                <InfoContainer icon={<CircularProgress />} title={t("inputFeatures.loading")} />
              ) : workflowFormik?.values?.feature_mappings?.length === 0 ? (
                <InfoBlock text={t("inputFeatures.emptyState")} bordered type="default" alignText="center" />
              ) : (
                <TemplateFeaturesTable featuresList={templateData?.schema?.input_features} />
              )}
            </Grid>
          )}

          {/* Output features */}
          {templateData?.schema && templateData?.schema?.output_features?.length > 0 && (
            <Grid item className={styles.baseBlock}>
              <Grid item className={styles.blockTitle}>
                <Typography variant="label-bold" variantColor={2} data-order>
                  {order.slice(0, 4).filter(Boolean).length}
                </Typography>

                <Typography variant="a">{t("labels.expectedOutputs")}</Typography>
              </Grid>

              <TemplateFeaturesTable featuresList={templateData?.schema?.output_features} />
            </Grid>
          )}
        </Fragment>
      )}
    </Grid>
  )
}

export function IntegrationTesting(props: Readonly<BuiltinScriptTestingProps>): ReactElement {
  const {
    handleRunTestClicked,
    isLoading,
    testingLogsProps: {
      logs,
      consoleLogs,
      isTestScriptMutationLoading,
      isConsoleExpanded,
      isLogsExpanded,
      setIsLogsExpanded,
      setIsConsoleExpanded,
    },
  } = props

  const { values, setFieldValue, handleChange, handleBlur } = useFormikContext<{
    testFeatures: Record<string, ScriptTestFeature>
  }>()

  const { t } = useTranslation("scripts", {
    keyPrefix: "builtinScript.components.testing",
  })

  const rowsPerPage = 3

  const tableData = Object.values(values.testFeatures)

  const [page, setPage] = useState<number>(0)
  const [search, setSearch] = useState<string | undefined>(undefined)

  const handleRemove = (id: string): void => {
    const newTestFeatures = { ...values.testFeatures }

    delete newTestFeatures[id]

    setFieldValue("testFeatures", newTestFeatures)

    // go back one page automatically if the last page has no more items to show
    Object.keys(newTestFeatures).length <= page * rowsPerPage && setPage(page - 1)
  }

  const handleAddTestFeature = (): void => {
    const id = uuidv4()

    setFieldValue("testFeatures", {
      [id]: {
        id,
        name: "",
        value: "",
        original: false,
        type: ScriptSchemaFeature.type.TEXT,
        is_required: false,
      },
      ...values.testFeatures,
    })

    setSearch("")
    setPage(0)
  }

  // Helper function to infer type from value
  const inferType = (value: unknown): ScriptSchemaFeature.type => {
    // console.log(value, typeof value)

    switch (typeof value) {
      case "boolean":
        return ScriptSchemaFeature.type.BOOLEAN
      case "number":
        return ScriptSchemaFeature.type.NUMBER
      case "string":
        return ScriptSchemaFeature.type.TEXT
      case "object":
        if (value === null) return ScriptSchemaFeature.type.UNDEFINED
        return ScriptSchemaFeature.type.TEXT
      default:
        return Array.isArray(value) ? ScriptSchemaFeature.type.LIST : ScriptSchemaFeature.type.TEXT
    }
  }

  // Define the configuration object for ScriptSchemaFeatureMergeOptions
  const ScriptSchemaFeatureMergeOptions: MergeOptions<ScriptTestFeature, ScriptSchemaFeature.type> = {
    keyField: "name",
    valueField: "value",
    typeField: "type",
    lockField: "original",
    lockValue: false,
    inferType: inferType,
    createNewItem: (key, value, type) => ({
      id: uuidv4(),
      name: key,
      value: `${value}`,
      type: type,
      original: false,
      is_required: false,
    }),
  }
  // Create your original function as a simple wrapper
  const convertJsonToTableData = (jsonText: string, existingFeatures: ScriptTestFeature[]): ScriptTestFeature[] => {
    return mergeJsonWithArray(jsonText, existingFeatures, ScriptSchemaFeatureMergeOptions)
  }

  const columns: MRT_ColumnDef<ScriptTestFeature>[] = [
    {
      header: t("table.headers.input"),
      accessorKey: "name",
      Cell: ({ row }) => {
        const value = row.original.name

        return (
          <Grid item xs={12}>
            {!row.original.original ? (
              <InputText
                fullWidth
                hideDescription
                size="small"
                placeholder={t("table.columns.input.placeholders.inputName")}
                id={`testFeatures.${row.original.id}.name`}
                key={row.original.id}
                value={value}
                handleChange={handleChange}
                handleBlur={handleBlur}
              />
            ) : (
              <Typography variant="p">{value}</Typography>
            )}
          </Grid>
        )
      },
    },
    {
      header: t("table.headers.type"),
      accessorKey: "type",
      Cell: ({ row }) => {
        return (
          <Grid item container xs={12} flexWrap="nowrap">
            {row.original.original ? (
              <Typography style={{ textTransform: "capitalize" }} variant="p">
                {row.original.type?.toLowerCase()}
              </Typography>
            ) : (
              <Select
                fullWidth
                hideDescription
                type="text"
                size="small"
                placeholder={t("table.columns.type.placeholders.chooseType")}
                optionsWithValues={Object.values(ScriptSchemaFeature.type).map((type) => ({
                  label: capitalize(type.toLowerCase()),
                  value: type,
                }))}
                id={`testFeatures.${row.original.id}.type`}
                value={row.original.type}
                handleChange={(e) => {
                  setFieldValue(`testFeatures.${row.original.id}.type`, e.target.value)

                  setFieldValue(
                    `testFeatures.${row.original.id}.value`,
                    typeCastBasedOnType(row.original.value, e.target.value as ScriptSchemaFeature.type),
                  )
                }}
              />
            )}
          </Grid>
        )
      },
    },
    {
      header: t("table.headers.required"),
      accessorKey: "required",
      Cell: ({ row }) => {
        return <Tick variant={row.original.is_required ? "positive" : "base"} />
      },
    },
    {
      header: t("table.headers.value"),
      accessorKey: "value",
      Cell: ({ row }) => {
        if (row.original.type === ScriptSchemaFeature.type.LIST) {
          return <Typography variant="a">{t("table.columns.value.cannotViewList")}</Typography>
        }

        if (row.original.type === ScriptSchemaFeature.type.UNDEFINED) {
          return
        }

        return (
          <Grid item xs={12}>
            {row.original.type === ScriptSchemaFeature.type.BOOLEAN ? (
              <Select
                fullWidth
                hideDescription
                type="text"
                size="small"
                options={["True", "False"]}
                placeholder={t("table.columns.value.placeholders.chooseValue")}
                id={`testFeatures.${row.original.id}.value`}
                value={row.original.value ? "True" : "False"}
                handleChange={(e) => {
                  setFieldValue(`testFeatures.${row.original.id}.value`, e.target.value === "True" ? true : false)
                }}
              />
            ) : (
              <InputText
                fullWidth
                hideDescription
                size="small"
                placeholder={t("table.columns.value.placeholders.value")}
                id={`testFeatures.${row.original.id}.value`}
                key={row.original.id}
                value={row.original.value}
                handleChange={handleChange}
                handleBlur={handleBlur}
              />
            )}
          </Grid>
        )
      },
    },
    {
      header: t("table.headers.action"),
      accessorKey: "action",
      Cell: ({ row }) => {
        const isButtonDisabled = row.original.is_required && row.original.original

        return (
          <Grid item xs={12} container flexWrap={"nowrap"}>
            <Button
              tooltip={isButtonDisabled ? t("table.columns.action.deleteTooltip") : ""}
              tooltipPlacement="top"
              variant={"ghost"}
              size="small"
              onClick={() => handleRemove(row.original.id)}
              className={styles.iconButton}
              disabled={isButtonDisabled}
            >
              <DeleteOutline
                style={{
                  fontSize: "16px",
                  fill: !isButtonDisabled
                    ? "var(--negative-icon-default) !important"
                    : "var(--disabled-icon-default) !important",
                }}
              />
            </Button>
          </Grid>
        )
      },
    },
  ]

  return (
    <Grid container item xs={12} flexDirection={"column"}>
      <Grid item container xs flexDirection={"column"} gap={0.5} flexWrap={"nowrap"} flex={1}>
        <Grid container item xs className={styles.builtInScriptTestingContainer}>
          <TabularJsonTable
            columns={columns}
            data={tableData}
            setData={(jsonText: string) => {
              setFieldValue(
                "testFeatures",
                convertJsonToTableData(jsonText, tableData).reduce(
                  (acc, feature) => {
                    const id = uuidv4()

                    const newObj = { ...feature }

                    newObj.id = id

                    return {
                      ...acc,
                      [id]: {
                        ...newObj,
                      },
                    }
                  },
                  {} as Record<string, ScriptTestFeature>,
                ),
              )
            }}
            headerActions={[
              <Button
                variant="primary-inverse"
                size="small"
                onClick={handleRunTestClicked}
                className={styles.runTestButton}
                isLoading={isLoading}
              >
                <PlayArrowIcon style={{ fontSize: 16 }} />
                <Typography variant="a" style={{ color: "var(--primary-inverse-text-default)" }}>
                  {t("actions.runTest")}
                </Typography>
              </Button>,
            ]}
            actions={[
              <Button variant="secondary" size="small" onClick={handleAddTestFeature}>
                {t("actions.addFeature")}
              </Button>,
            ]}
            rowsPerPage={rowsPerPage}
            page={page}
            setPage={setPage}
            search={search}
            setSearch={setSearch}
          />
        </Grid>

        <ScriptTestingLogs
          logs={logs}
          consoleLogs={consoleLogs}
          isTestScriptMutationLoading={isTestScriptMutationLoading}
          isConsoleExpanded={isConsoleExpanded}
          isLogsExpanded={isLogsExpanded}
          setIsConsoleExpanded={setIsConsoleExpanded}
          setIsLogsExpanded={setIsLogsExpanded}
        />
      </Grid>
    </Grid>
  )
}
